<template>
  <div
    class="product-details text-charcoal-default"
    :data-test-id="productDetailsTestId('productDetailContent')"
    data-fs="productDetailContent"
  >
    <Stack direction="col" gap="sm" :breakpoints="{ gap: { md: 'md' } }">
      <div v-if="product?.[0]">
        <ProductStatusBanner v-if="deal == true" type="deal-1line" class="ui-mb-xs" />
        <ProductStatusBanner v-if="clearance == true" type="clearance" class="ui-mb-xs" />
        <Heading
          size="xs"
          is="h1"
          class="my-[6px]"
          :data-test-id="productDetailsTestId('productNamePdp')"
          data-fs="productNamePdp"
        >
          {{ product[0].name }}
        </Heading>
        <Text is="p" size="sm" :data-test-id="productDetailsTestId('productCode')" data-fs="productCode">
          Product code: {{ product[0].sku }}
        </Text>
      </div>

      <div v-if="containsSustainabilityIcon">
        <SustainabilityIcons v-if="product" :custom-fields="product[0].custom_fields" />
      </div>

      <!-- flag 1, 2-->
      <Stack
        v-if="availabilityFlag !== '4' && availabilityFlag !== '3'"
        align="start"
        gap="xs"
        justify="between"
        direction="col"
        :data-test-id="productDetailsTestId('pdpAvailibilityStatus')"
        class="w-full border-b-sm border-t-sm py-md border-grey-light"
        :breakpoints="{ direction: { md: 'row' }, gap: { md: 'none' } }"
      >
        <Stack align="start" class="!w-full md:!w-2/5">
          <Icon name="truck" :size="16" class="mt-3xs" />
          <Stack direction="col" gap="none" :data-test-id="productDetailsTestId('freeDelivery')" data-fs="freeDelivery">
            <Text size="sm" weight="bold">FREE local delivery</Text>
            <Text size="sm">Restrictions apply*</Text>
          </Stack>
        </Stack>
        <Stack v-if="selectedBranch" align="start" class="!w-full md:!w-3/5">
          <Icon name="pointer-blue" :size="16" class="mt-3xs" />
          <Stack
            direction="col"
            gap="none"
            :data-test-id="productDetailsTestId('pdpFreeCollection')"
            data-fs="freeClickAndCollect"
          >
            <Text size="sm" weight="bold">FREE Click & Collect from {{ selectedBranch.name }} </Text>
            <Stack align="baseline" gap="none">
              <div
                :class="[
                  'w-[10px] h-[10px] mr-2xs rounded-round',
                  availabilityFlag === '2' ? 'bg-orange-default' : 'bg-green-default',
                ]"
              />
              <Text size="sm" :data-test-id="productDetailsTestId('availability')" data-fs="availability" class="wrap">
                {{ availabilityReason }}
                <Text
                  v-if="immediatelyCollectable"
                  weight="bold"
                  size="sm"
                  class="ui-text-mkm-blue-light cursor-pointer ui-bg-transparent ui-z-10"
                  @click="setBranchTray()"
                >
                  See opening hours
                </Text>
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <section v-if="availabilityFlag === '4'" class="border-b-sm border-t-sm py-md border-grey-light !w-full">
        <Stack align="start" gap="xs">
          <Icon name="package" :size="16" class="mt-3xs" />
          <Stack direction="col" gap="none" :data-test-id="productDetailsTestId('freeDelivery')" class="!w-full">
            <Text size="sm" weight="bold">FREE delivery available</Text>
            <Text size="sm">{{ availabilityReason }} </Text>
            <Text
              size="sm"
              data-test-id="findOutMore"
              data-fs="findOutMore"
              @click="setDropshipTray"
              weight="bold"
              class="cursor-pointer text-mkm-blue-light"
            >
              Find out more
            </Text>
          </Stack>
        </Stack>
      </section>

      <section
        v-if="selectedBranch && availabilityFlag === '3'"
        class="border-b-sm border-t-sm py-md border-grey-light w-full"
      >
        <CardUSP
          availability="outOfStock"
          :availability-reason="availabilityReason"
          :branch="selectedBranch.name"
          :branch-number="selectedBranch.contacts.phone"
          :data-test-id="productDetailsTestId('outOfStock')"
          data-fs="pdpOutOfStock"
        />
      </section>

      <Stack v-if="availabilityFlag !== '2' && availabilityFlag !== '3'" direction="col" align="start">
        <Text v-if="selectedBranch && !user">
          <button
            @click="setLoginTray"
            class="text-mkm-blue-light font-bold"
            :data-test-id="productDetailsTestId('signInLink')"
            data-fs="signInLink"
          >
            Sign in
          </button>
          for your account price</Text
        >

        <!-- Show Default BR Price -->
        <Stack v-if="!user && selectedBranch" direction="col" gap="2xs" align="start">
          <Stack gap="2xs" align="baseline">
            <Price :unit="unit" :price="productPrice.calculated" :clearance="clearance" />
          </Stack>
          <Stack v-if="offerPrice !== priceVatExc" gap="2xs" align="baseline">
            <Text
              size="md"
              weight="semi"
              class="text-charcoal-light"
              :data-test-id="productDetailsTestId('productPrice')"
              data-fs="productPrice"
              >{{ toCurrency({ value: Number(isVatIncluded ? offerPriceIncVat : offerPrice) }) }}
            </Text>
            <Text
              size="xs"
              weight="semi"
              class="nowrap text-charcoal-light"
              :data-test-id="productDetailsTestId('offerUnit')"
              data-fs="offerUnit"
              >{{ productUnitTranslation(`${offerUnit}` as ProductDetailsType) }}</Text
            >
          </Stack>
        </Stack>

        <!-- Show User Specific Price -->
        <Stack v-if="user && price" direction="col" gap="2xs" align="start">
          <Stack align="center" gap="none" :data-test-id="productDetailsTestId('yourPrice')" data-fs="yourPrice">
            <Text v-if="user" weight="bold">{{ user.profile.first_name }}</Text>
            <Text class="nowrap">&nbsp;your price is</Text>
          </Stack>
          <Stack gap="2xs" align="baseline">
            <Price :unit="unit" :price="productPrice.calculated" :clearance="clearance" />
          </Stack>
          <Stack gap="2xs" align="baseline" v-if="offerPrice !== priceVatExc">
            <Text
              size="md"
              weight="semi"
              class="text-charcoal-light"
              :data-test-id="productDetailsTestId('productPrice')"
              data-fs="productPrice"
            >
              {{ toCurrency({ value: isVatIncluded ? price.offer_price_inc_vat : price.offer_price }) }}
            </Text>
            <Text
              size="xs"
              weight="semi"
              class="nowrap text-charcoal-light"
              :data-test-id="productDetailsTestId('offerUnit')"
            >
              {{ productUnitTranslation(`${offerUnit}` as ProductDetailsType) }}
            </Text>
          </Stack>
        </Stack>

        <!-- User No Terms -->
        <Stack direction="col" gap="2xs" align="start" v-if="user && !price">
          <Stack align="center" gap="none" :data-test-id="productDetailsTestId('yourPrice')" data-fs="yourPrice">
            <Text v-if="user" weight="bold">{{ user.profile.first_name }}</Text>
            <Text class="nowrap">&nbsp;your price is</Text>
          </Stack>
          <Stack gap="2xs" align="baseline">
            <Price :unit="unit" :price="productPrice.calculated" :clearance="clearance" />
          </Stack>

          <Stack v-if="offerPrice !== priceVatExc" gap="2xs" align="baseline">
            <Text
              size="md"
              weight="semi"
              class="text-charcoal-light"
              :data-test-id="productDetailsTestId('productPrice')"
              data-fs="productPrice"
              >{{ toCurrency({ value: Number(isVatIncluded ? offerPriceIncVat : offerPrice) }) }}
            </Text>
            <Text
              size="xs"
              weight="semi"
              class="nowrap text-charcoal-light"
              :data-test-id="productDetailsTestId('offerUnit')"
              data-fs="offerUnit"
            >
              {{ productUnitTranslation(`${offerUnit}` as ProductDetailsType) }}
            </Text>
          </Stack>
        </Stack>

        <!-- Show Select Branch Button -->
        <Stack v-if="!selectedBranch" direction="col" gap="xs">
          <Button
            size="md"
            variant="secondary"
            :data-test-id="productDetailsTestId('selectBranchBtn')"
            @click="handleBranchSelect"
            data-fs="pdpBranchSelect"
          >
            Select a branch
          </Button>
          <Text>to see available stock and prices</Text>
        </Stack>
      </Stack>

      <Stack
        v-if="availabilityFlag === '2'"
        direction="col"
        gap="xs"
        class="border-xl border-mkm-blue-default p-xs"
        :data-test-id="productDetailsTestId('quoteEnquiry')"
        data-fs="pdpQuoteEnquiry"
      >
        <Text size="sm"> {{ MESSAGES.PRODUCT_ENQUIRY }} </Text>
        <Text v-if="selectedBranch" weight="bold" size="sm">
          Call MKM {{ selectedBranch.name }} on
          <a :href="'tel:' + selectedBranch.contacts.phone" class="cursor-pointer text-mkm-blue-light">
            {{ selectedBranch.contacts.phone }}
          </a>
        </Text>
      </Stack>

      <!-- Quantity Calculation Option -->
      <Stack
        v-if="(areaCoverage || volumeCoverage) && canAddToBasket"
        tag="button"
        gap="2xs"
        align="center"
        data-test-id="openQtyCalculatorTrayBtn"
        class="text-mkm-blue-light font-bold"
        @click="setQtyCalculatorTray"
      >
        <Icon name="calculator" :size="16" /> Calculate how many you need
      </Stack>

      <Stack
        v-if="canAddToBasket"
        gap="sm"
        align="start"
        class="nowrap w-full md:w-3/5"
        direction="col"
        :breakpoints="{ direction: { lg: 'row' }, align: { lg: 'center' }, gap: { md: 'md' } }"
      >
        <CardQuantityControl
          @update:quantity="handleQuantityUpdate"
          :quantity="productQuantity"
          :data-test-id="productDetailsTestId('CardQuantityControl')"
        />
        <Button
          size="lg"
          class="!w-full flex justify-center text-center"
          :disabled="isAdding || productQuantity === 0"
          :data-test-id="productDetailsTestId('addToBasket')"
          @click="addItemToBasket"
          data-fs="pdpAddToBasketButton"
        >
          ADD TO BASKET
          <Spinner v-show="isAdding" />
        </Button>
      </Stack>
      <Stack
        v-if="selectedBranchId && isTally && availabilityFlag !== '3'"
        gap="md"
        align="start"
        class="nowrap w-full md:w-3/5"
        direction="col"
        :breakpoints="{ direction: { lg: 'row' }, align: { lg: 'center' } }"
      >
        <Button
          size="lg"
          variant="secondary"
          :data-test-id="productDetailsTestId('chooseQuantity')"
          data-fs="pdpChooseQuantity"
          @click="setTallyTray"
        >
          CHOOSE QUANTITY
        </Button>
      </Stack>
    </Stack>
  </div>

  <LazyQtyCalculatorTray
    v-if="areaCoverage || volumeCoverage"
    :product="product[0]"
    :product-details="productDetails"
    :areaCoverage="Number(areaCoverage)"
    :volumeCoverage="Number(volumeCoverage)"
    :price="productPrice.base"
    @update:qty="productQuantity = $event"
  />
</template>
<script lang="ts" setup>
import { toCurrency } from "mkm-avengers";
import { MESSAGES } from "~/constants/messages";
import { productUnitTranslation, type ProductDetailsType } from "~/utils/productUnitTranslation";

const props = defineProps<{
  product: any;
  price: any;
  productDetails: any;
  productDetailsViewMobile: boolean;
}>();

const { user } = useUser();
const { selectedBranchId, selectedBranch } = useBranches();
const { addCartItemQuantity } = useCart();

const {
  isVatIncluded,
  setAutoBranchSelect,
  setTallyTray,
  setDropshipTray,
  setBasketTray,
  setBranchTray,
  setLoginTray,
  setQtyCalculatorTray,
} = useUIState();

const areaCoverage = ref<number>();
const volumeCoverage = ref<number>();
const priceVatInc = ref<string>("");
const priceVatExc = ref<string>("");
const offerPrice = ref<string>("");
const offerPriceIncVat = ref<string>("");
const unit = ref<string>("");
const offerUnit = ref<string>("");
const availabilityFlag = ref<string>("");
const availabilityReason = ref<string>("");
const clearance = ref<boolean>(false);
const deal = ref<boolean>(false);
const isTally = ref<boolean>(false);
const productQuantity = ref<number>(1);
const isAdding = ref<boolean>(false);

const immediatelyCollectable = computed(() => availabilityReason.value.includes("1 hour"));

const canAddToBasket = computed(
  () => selectedBranch.value && !isTally.value && availabilityFlag.value !== "2" && availabilityFlag.value !== "3",
);

const productPrice = computed(() => {
  const price = props.price;
  const canUseBackendPrice = user.value && price;

  return {
    calculated:
      user.value && price
        ? isVatIncluded.value
          ? price.price_inc_vat
          : price.price
        : Number(isVatIncluded.value ? priceVatInc.value : priceVatExc.value),

    base: canUseBackendPrice ? price.price : priceVatExc.value,
  };
});

const containsSustainabilityIcon = computed(() => {
  if (props.product && Array.isArray(props.product) && props.product.length > 0) {
    const product = props.product[0];

    if (product.custom_fields && Array.isArray(product.custom_fields))
      return product.custom_fields.some((obj: any) => obj.name.includes("icon"));
  }
  return false;
});

const getCustomField = (fieldName: string, object: any) =>
  object?.custom_fields?.find((f: any) => f.name === fieldName)?.value;

areaCoverage.value = Number(getCustomField("coverage_area_(m2)_per_unit_sold", props.product?.[0]));
volumeCoverage.value = Number(getCustomField("cubic_volume", props.product?.[0]));

const handleQuantityUpdate = (quantity: number) => (productQuantity.value = quantity);

const productDetailsTestId = (id: string) => (props.productDetailsViewMobile ? `mobile-${id}` : id);

const fetchProductDetailsAndSetPrices = () => {
  if (!props.productDetails || !Array.isArray(props.productDetails.customAttrs)) return;

  const attrs = props.productDetails.customAttrs;

  priceVatInc.value = extractAttributeValue(attrs, "price_inc_vat") || "";
  priceVatExc.value = extractAttributeValue(attrs, "price") || "";
  unit.value = extractAttributeValue(attrs, "unit") || "";

  offerPrice.value = extractAttributeValue(attrs, "offer_price") || "";
  offerPriceIncVat.value = extractAttributeValue(attrs, "offer_price_inc_vat") || "";
  offerUnit.value = extractAttributeValue(attrs, "offer_unit") || "";

  availabilityFlag.value = extractAttributeValue(attrs, "availability_flag") || "";
  availabilityReason.value = extractAttributeValue(attrs, "availability_reason") || "";

  isTally.value = extractAttributeValue(attrs, "is_tally") !== "0";
  clearance.value = extractAttributeValue(attrs, "clearance") !== "0";
  deal.value = extractAttributeValue(attrs, "deal") !== "0";
};

fetchProductDetailsAndSetPrices();

const handleBranchSelect = () => {
  setAutoBranchSelect(false);
  setBranchTray();
};

const addItemToBasket = async () => {
  isAdding.value = true;
  await addCartItemQuantity(props.product[0].sku, productQuantity.value)
    .then(() => (isAdding.value = false))
    .then(() => setBasketTray(true));
};

watch(selectedBranchId, (newBranchId) => {
  if (newBranchId) fetchProductDetailsAndSetPrices();
});

watch(() => props.productDetails.customAttrs, fetchProductDetailsAndSetPrices, { immediate: true });
</script>

<style>
@media (min-width: 768px) {
  .product-details {
    position: sticky;
    top: 16px;
    width: 50%;
  }
}
.product-details {
  background-color: white;
  width: 100%;
  padding: 25px;
  box-shadow: 0 4px 3px rgba(0, 0, 0, 0.25);
  border: 1px solid #f0f0f0;
}
</style>
